import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import { AnswerKeyGate, VerdictBanner, readAnswerKeyGate } from 'components';

import { listBenchmarkRollup } from '../ScenarioBuilder/api';
import {
  cloneAssessmentScenario,
  createAssessmentScenario,
  deleteAssessmentScenario,
  listAssessmentScenarios,
} from './api';
import { EllipsisText, parseJsonObject, ScenarioStatusTag } from './shared';
import { AssessmentScenario } from './types';

type Props = {
  onOpen: (code: string) => void;
};

type CloneState = {
  source: AssessmentScenario;
} | null;

/**
 * The fleet's fitness sentence, counted rather than asserted.
 *
 * An admin choosing a definition to attach was shown a green ACTIVE badge and
 * nothing else, while the validity gate had failed most of the fleet. The
 * count is stated as "of the ones we could check", because the rollup only
 * covers answer keys tagged to a blueprint family and claiming a total we did
 * not measure would be the same mistake in the other direction.
 */
const fleetSentence = (checked: number, failing: number, unsigned: number) => {
  if (!checked || !failing) return '';
  const subject =
    checked === failing
      ? `All ${failing} active answer keys`
      : `${failing} of the ${checked} active answer keys`;
  const signed =
    unsigned === failing
      ? 'None of them has a recorded override.'
      : unsigned
        ? `${unsigned} of them have no recorded override.`
        : 'Each has a recorded override.';
  return (
    `${subject} that could be checked fail the validity gate. Sessions ` +
    `attached to them are graded by an answer key the gate calls unfit to ` +
    `tell skill from clicking. ${signed}`
  );
};

const ScenarioCatalog: React.FC<Props> = ({ onOpen }) => {
  const [rows, setRows] = useState<AssessmentScenario[]>([]);
  // code -> quality-audit verdict, from the answer-key rollup (one admin read).
  const [gates, setGates] = useState<Record<string, AnswerKeyGate | null>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneState, setCloneState] = useState<CloneState>(null);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [cloneForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await listAssessmentScenarios({ q: query || undefined, status }));
    } catch (requestError) {
      setError((requestError as Error).message);
    } finally {
      setLoading(false);
    }
  }, [query, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let active = true;
    listBenchmarkRollup()
      .then(rollup => {
        if (!active) return;
        const next: Record<string, AnswerKeyGate | null> = {};
        rollup.forEach(row => {
          const gate = readAnswerKeyGate(row);
          if (gate) next[row.code] = gate;
        });
        setGates(next);
      })
      // A catalogue that cannot read the gate says nothing about it rather
      // than implying every definition is fit.
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const activeRows = rows.filter(row => row.status_label === 'active');
  const checkedRows = activeRows.filter(row => gates[row.code]);
  const failingRows = checkedRows.filter(
    row =>
      gates[row.code]?.state === 'blocked' ||
      gates[row.code]?.state === 'overridden',
  );
  const unsignedRows = failingRows.filter(
    row => gates[row.code]?.state === 'blocked',
  );
  const fleetWarning = fleetSentence(
    checkedRows.length,
    failingRows.length,
    unsignedRows.length,
  );

  const createScenario = async () => {
    try {
      const values = await createForm.validateFields();
      setSaving(true);
      const created = await createAssessmentScenario({
        code: values.code.trim(),
        name: values.name.trim(),
        model_version: values.model_version.trim(),
        params: parseJsonObject(values.params, 'Parameters'),
      });
      message.success(`Created draft ${created.code}.`);
      setCreateOpen(false);
      createForm.resetFields();
      await load();
      onOpen(created.code);
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openClone = (source: AssessmentScenario) => {
    setCloneState({ source });
    cloneForm.setFieldsValue({
      code: `${source.code}-copy`,
      name: `${source.name} (copy)`,
      model_version: source.model_version,
    });
  };

  const cloneScenario = async () => {
    if (!cloneState) return;
    try {
      const values = await cloneForm.validateFields();
      setSaving(true);
      const created = await cloneAssessmentScenario(cloneState.source.code, {
        code: values.code.trim(),
        name: values.name.trim(),
        model_version: values.model_version.trim(),
      });
      message.success(`Cloned definition into draft ${created.code}.`);
      setCloneState(null);
      await load();
      onOpen(created.code);
    } catch (requestError) {
      if ((requestError as any)?.errorFields) return;
      message.error((requestError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const removeScenario = async (row: AssessmentScenario) => {
    try {
      await deleteAssessmentScenario(row.code);
      message.success(`Deleted unlinked draft ${row.code}.`);
      await load();
    } catch (requestError) {
      message.error((requestError as Error).message);
    }
  };

  const columns: any[] = [
    {
      title: 'Assessment',
      dataIndex: 'code',
      fixed: 'left',
      width: 235,
      render: (_: string, row: AssessmentScenario) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={row.name} placement="topLeft">
            <Typography.Link
              className="assessment-ellipsis"
              onClick={() => onOpen(row.code)}
            >
              {row.name}
            </Typography.Link>
          </Tooltip>
          <EllipsisText code value={row.code} />
        </Space>
      ),
    },
    {
      title: 'Lifecycle',
      width: 130,
      render: (_: unknown, row: AssessmentScenario) => (
        <Space direction="vertical" size={2}>
          <ScenarioStatusTag label={row.status_label} editable={row.editable} />
          {!row.editable && row.status_label === 'draft' && (
            <Tag>LINKED / LOCKED</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Quality audit',
      width: 210,
      // The fitness of the answer key, on the row where a definition is
      // chosen. Blank where the audit has no reading for this definition --
      // silence here means unknown, and the banner above says so.
      render: (_: unknown, row: AssessmentScenario) =>
        gates[row.code] ? (
          <VerdictBanner gate={gates[row.code]} variant="compact" />
        ) : (
          <Typography.Text type="secondary">Not checked here</Typography.Text>
        ),
    },
    {
      title: 'Model version',
      dataIndex: 'model_version',
      width: 200,
      render: (value: string) => <EllipsisText code value={value} />,
    },
    {
      title: 'Definition',
      width: 230,
      render: (_: unknown, row: AssessmentScenario) => (
        <Space wrap size={[4, 4]} className="assessment-tag-group">
          <Tag>{row.instrument_count} instruments</Tag>
          <Tag>{row.period_count} periods</Tag>
          <Tag>{row.benchmark_count} answer-key rows</Tag>
          <Tag>{row.news_score_count} news</Tag>
        </Space>
      ),
    },
    {
      title: 'Runtime',
      width: 125,
      render: (_: unknown, row: AssessmentScenario) => (
        <Tag color={row.runtime_ready ? 'green' : 'orange'}>
          {row.runtime_ready ? 'BASE READY' : 'INCOMPLETE'}
        </Tag>
      ),
    },
    {
      title: 'Usage',
      width: 130,
      render: (_: unknown, row: AssessmentScenario) => (
        <Space direction="vertical" size={0}>
          <span>{row.linked_session_count} sessions</span>
          <span>{row.result_count} results</span>
        </Space>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 230,
      render: (_: unknown, row: AssessmentScenario) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onOpen(row.code)}
          >
            Open
          </Button>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => openClone(row)}
          >
            Clone
          </Button>
          <Popconfirm
            title="Delete this draft definition?"
            description="Only an unlinked draft can be deleted. This cannot be undone."
            disabled={!row.editable}
            onConfirm={() => removeScenario(row)}
          >
            <Button
              size="small"
              danger
              disabled={!row.editable}
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      // ANSWER KEYS (D12). "Assessment model" was the third name this one
      // thing has worn -- the tab above already says answer keys, and a card
      // that disagrees with its own tab teaches people the two are different.
      title="Answer keys"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            New draft
          </Button>
        </Space>
      }
    >
      {fleetWarning && (
        <Alert
          className="assessment-callout"
          showIcon
          type="warning"
          title="Active answer keys are failing the validity gate"
          description={fleetWarning}
        />
      )}
      <Alert
        className="assessment-callout"
        showIcon
        type="info"
        title="Management surface only"
        description="Definitions use the existing assessment schema and runtime preflight. Active definitions and any draft already attached to a session are read-only here."
      />
      <Space className="assessment-filter-row" wrap>
        <Input.Search
          allowClear
          placeholder="Search code, name, or model version"
          style={{ width: 340 }}
          onSearch={setQuery}
        />
        <Select
          allowClear
          placeholder="All lifecycle states"
          style={{ width: 190 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
          ]}
        />
      </Space>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Table
        rowKey="id"
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1660 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title="Create assessment draft"
        open={createOpen}
        okText="Create draft"
        confirmLoading={saving}
        onOk={createScenario}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ params: '{}' }}
        >
          <Form.Item
            name="code"
            label="Stable scenario code"
            rules={[
              { required: true },
              {
                pattern: /^[A-Za-z0-9._-]+$/,
                message: 'Use letters, numbers, dot, underscore, or hyphen.',
              },
            ]}
          >
            <Input placeholder="the-turn-v2" />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="model_version"
            label="Model version"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="params" label="Stored parameters (JSON object)">
            <Input.TextArea
              autoSize={{ minRows: 4, maxRows: 10 }}
              spellCheck={false}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Clone ${cloneState?.source.code || ''}`}
        open={Boolean(cloneState)}
        okText="Create definition-only clone"
        confirmLoading={saving}
        onOk={cloneScenario}
        onCancel={() => setCloneState(null)}
        destroyOnHidden
      >
        <Alert
          className="assessment-callout"
          type="info"
          showIcon
          title="Only definition data is copied"
          description="Session links, captured evidence, job state, and results are intentionally excluded. The clone is always a new draft."
        />
        <Form form={cloneForm} layout="vertical">
          <Form.Item
            name="code"
            label="New scenario code"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="name" label="New name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="model_version"
            label="Model version"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ScenarioCatalog;
